const TERMIN_LINK = "https://calendly.com/d/cxxk-vc9-57d/kurzberatung-abstimmungstermin";

/**
 * Was ein Betrieb sieht, der die Patientenanfragen noch nicht freigeschaltet hat.
 *
 * Der Eintrag in der Navigation bleibt für alle stehen; nur das Ziel unterscheidet sich.
 * So entdeckt ein Kunde das Feature dort, wo er es später auch benutzen wird.
 *
 * Der Aufbau folgt dem abgenommenen Entwurf: Überschrift mit Terminknopf, darunter drei
 * Punkte zwischen zwei Linien, dann der Ablauf von der Kundenwebsite zu kandidat.io.
 *
 * Bewusst ohne Preis: Solange keiner feststeht, lädt die Seite zum Gespräch ein. Die
 * Zahlen und Namen im Bild sind erfunden.
 */
const Punkt = ({ titel, text, children }: { titel: string; text: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-[1_1_240px] items-start gap-3">
    <span className="mt-0.5 flex-none text-[#0099ce]">{children}</span>
    <p className="m-0 text-sm leading-relaxed text-[#41495a]">
      <strong className="font-semibold text-[#1a202c]">{titel}</strong> {text}
    </p>
  </div>
);

/** Die Kopfleiste eines Browserfensters, mit der Adresse darin. */
const Fensterkopf = ({ adresse }: { adresse: string }) => (
  <div className="flex items-center gap-2 border-b border-[#dfe3ea] bg-[#f1f3f7] px-2.5 py-2">
    <span className="flex flex-none gap-1">
      <span className="h-2 w-2 rounded-full bg-[#d8dce4]" />
      <span className="h-2 w-2 rounded-full bg-[#d8dce4]" />
      <span className="h-2 w-2 rounded-full bg-[#d8dce4]" />
    </span>
    <span className="min-w-0 flex-1 truncate rounded border border-[#e3e6ec] bg-white px-2 py-0.5 text-[10px] text-[#596274]">{adresse}</span>
  </div>
);

const Feld = ({ beschriftung, wert, pflicht }: { beschriftung: string; wert: string; pflicht?: boolean }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
    <span className="text-[8px] text-[#41495a]">
      {beschriftung} {pflicht && <span className="text-[#c0392b]">*</span>}
    </span>
    <div className="truncate rounded border border-[#d5dae3] bg-white px-[7px] py-[5px] text-[10px]">{wert}</div>
  </div>
);

const Zeile = ({
  name,
  standort,
  eingang,
  status,
  farbe,
  rahmen,
  hervorgehoben,
}: {
  name: string;
  standort: string;
  eingang: string;
  status: string;
  farbe: string;
  rahmen: string;
  hervorgehoben?: boolean;
}) => (
  <div className={`flex items-center gap-[9px] border-b border-[#eef0f5] px-[11px] py-2 last:border-b-0 ${hervorgehoben ? "bg-[#f0f9fd]" : ""}`}>
    <span className={`min-w-0 flex-[1.3] truncate text-[10px] ${hervorgehoben ? "font-semibold" : ""}`}>{name}</span>
    <span className="min-w-0 flex-[1.5] truncate text-[10px] text-[#41495a]">{standort}</span>
    <span className="min-w-0 flex-1 text-[10px] text-[#41495a]">{eingang}</span>
    <span className="min-w-0 flex-[0.9]">
      <span className="whitespace-nowrap rounded-full border px-1.5 py-px text-[8px] font-semibold" style={{ color: farbe, borderColor: rahmen }}>
        {status}
      </span>
    </span>
  </div>
);

export const PatientRequestsTeaser = () => (
  <div className="p-6">
    <div className="mx-auto flex max-w-[1320px] flex-col gap-7">
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 text-[30px] font-bold tracking-tight">Patientenanfragen</h1>
        <p className="m-0 text-[15px] text-[#596274]">Für Ihren Betrieb noch nicht freigeschaltet.</p>
      </div>

      <div className="flex flex-col gap-9 rounded-2xl border border-[#e3e6ec] bg-white p-10">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div className="flex min-w-0 flex-[1_1_520px] flex-col gap-2.5">
            <h2 className="m-0 text-[26px] font-semibold leading-snug tracking-tight">Alle Anfragen nach einem Pflegeplatz an einem Ort</h2>
            <p className="m-0 max-w-[620px] text-base leading-relaxed text-[#41495a]">
              Ein Formular auf Ihrer Website, und jede Anfrage landet in kandidat.io statt in einem Postfach.
            </p>
          </div>

          <div className="flex flex-none flex-col items-start gap-2">
            <a
              href={TERMIN_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center gap-2.5 rounded-lg bg-[#0099ce] px-6 py-3.5 text-[15px] font-semibold text-white no-underline hover:bg-[#0088b8]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M16 3v4M8 3v4M3 11h18" />
              </svg>
              Termin vereinbaren
            </a>
            <p className="m-0 text-[13px] text-[#596274]">15 Minuten mit Herrn Dörge</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-8 border-y border-[#eef0f5] py-[30px]">
          <Punkt titel="Übersicht über alle Anfragen." text="Gefiltert nach Standort und Phase, mit Suche. Jede Anfrage trägt eine Notiz.">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M8 9h8M8 13h8M8 17h5" />
            </svg>
          </Punkt>
          <Punkt titel="Nichts bleibt liegen." text="Was über Nacht unbearbeitet bleibt, meldet sich am Morgen beim zuständigen Standort.">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </Punkt>
          <Punkt titel="Auswertung." text="Wie viele Anfragen eingehen, wie sie sich auf Ihre Standorte verteilen, wie viele zum Einzug führen.">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
            </svg>
          </Punkt>
        </div>

        <div className="flex flex-wrap items-center gap-[22px]">
          <div className="flex min-w-0 flex-[1_1_400px] flex-col gap-2.5">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wider text-[#596274]">Ihre Website</p>

            <div className="overflow-hidden rounded-[9px] border border-[#dfe3ea] bg-white shadow-sm">
              <Fensterkopf adresse="seniorenzentrum-neuer-hof.de/pflegeplatz" />

              <div className="flex items-center justify-between gap-2.5 bg-[#2c6e4f] px-4 py-2.5">
                <span className="text-xs font-bold tracking-tight text-white">Seniorenzentrum Neuer Hof</span>
                <span className="flex gap-3">
                  <span className="text-[9px] text-[#d4e8dd]">Haus</span>
                  <span className="text-[9px] text-[#d4e8dd]">Pflege</span>
                  <span className="border-b-[1.5px] border-[#9ccfb4] pb-px text-[9px] text-white">Kontakt</span>
                </span>
              </div>

              <div className="p-4">
                {/* Nachbau des echten Formulars: gleiche Reihenfolge, gleiche Pflichtfelder.
                    Der Knopf ist grün, weil dort die Hausfarbe des Betriebs gilt. */}
                <div className="flex flex-col gap-[9px] rounded-md border border-[#e3e6ec] bg-white p-[13px]">
                  <div className="mb-0.5 flex flex-col gap-0.5">
                    <span className="text-[13px] font-medium text-[#1a202c]">Pflegeplatz anfragen</span>
                    <span className="text-[9px] text-[#7a8292]">Wir melden uns in der Regel am nächsten Werktag zurück.</span>
                  </div>

                  <div className="flex gap-2">
                    <Feld beschriftung="Vorname" wert="Andrea" />
                    <Feld beschriftung="Nachname" wert="Hoffmann" pflicht />
                  </div>
                  <div className="flex gap-2">
                    <Feld beschriftung="E-Mail" wert="a.hoffmann@example.de" pflicht />
                    <Feld beschriftung="Telefon" wert="0171 2345678" pflicht />
                  </div>

                  <span className="-mt-[3px] text-[8px] text-[#9aa2b1]">
                    Bitte mindestens eines von beiden ausfüllen, damit wir uns bei Ihnen zurückmelden können.
                  </span>

                  <div className="flex flex-col gap-[3px]">
                    <span className="text-[8px] text-[#41495a]">
                      Welcher Standort? <span className="text-[#c0392b]">*</span>
                    </span>
                    <div className="flex items-center justify-between rounded border border-[#d5dae3] bg-white px-[7px] py-[5px] text-[10px]">
                      <span>Seniorenzentrum Neuer Hof</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9aa2b1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </div>

                  <div className="flex flex-col gap-[3px]">
                    <span className="text-[8px] text-[#41495a]">Ihre Nachricht</span>
                    <div className="min-h-[26px] rounded border border-[#d5dae3] bg-white px-[7px] py-[5px] text-[10px] leading-snug text-[#41495a]">
                      Für meine Mutter, ab Dezember wird ein Platz gesucht.
                    </div>
                  </div>

                  <div className="mt-0.5 flex h-[26px] items-center justify-center rounded bg-[#2c6e4f]">
                    <span className="text-[10px] font-medium leading-none text-white">Anfrage absenden</span>
                  </div>

                  <span className="text-[8px] leading-snug text-[#9aa2b1]">
                    Pflichtfelder sind mit * gekennzeichnet. Es wird keine Angabe zum Pflegegrad abgefragt.
                  </span>
                </div>

                <span className="mt-3 block text-[8px] text-[#9aa2b1]">Das eingebettete Formular aus kandidat.io, in Ihrer Hausfarbe</span>
              </div>
            </div>
          </div>

          {/* Verbindet die beiden Fenster. Bricht die Reihe um, rutscht es in die Mitte
              und der Pfeil zeigt nach unten, damit der Ablauf weiterliest. */}
          <div className="flex flex-none items-center justify-center px-1.5 max-[1040px]:w-full max-[1040px]:py-1">
            <span className="flex h-[42px] w-[42px] items-center justify-center rounded-full border border-[#e3e6ec] bg-[#eef0f5] max-[1040px]:rotate-90">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#596274" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h13" />
                <path d="M13 6l6 6-6 6" />
              </svg>
            </span>
          </div>

          <div className="flex min-w-0 flex-[1.25_1_500px] flex-col gap-2.5">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wider text-[#596274]">In kandidat.io</p>

            <div className="overflow-hidden rounded-[9px] border border-[#dfe3ea] bg-white shadow-sm">
              <Fensterkopf adresse="app.kandidat.io/patientenanfragen" />

              <div className="flex bg-[#f9fafc]">
                <div className="flex w-[42px] flex-none flex-col items-center gap-3 bg-gradient-to-b from-[#3b46c4] to-[#2f9fe0] py-3">
                  <img src="../logo.svg" alt="" width="25" height="25" className="block flex-none" />
                  <span className="h-1" />
                  <svg width="15" height="15" viewBox="0 -960 960 960" fill="#ffffff" aria-hidden="true" className="opacity-80">
                    <path d="M160-120v-480l320-240 320 240v480H560v-280H400v280H160Z" />
                  </svg>
                  <svg width="15" height="15" viewBox="0 -960 960 960" fill="#ffffff" aria-hidden="true" className="opacity-80">
                    <path d="M720-400v-120H600v-80h120v-120h80v120h120v80H800v120h-80Zm-360-80q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47ZM40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Z" />
                  </svg>
                  <span className="flex h-[25px] w-7 flex-none items-center justify-center rounded-md bg-white/20">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fdf6b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                      <path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08v0c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66" />
                      <path d="m18 15-2-2" />
                      <path d="m15 18-2-2" />
                    </svg>
                  </span>
                  <svg width="15" height="15" viewBox="0 -960 960 960" fill="#ffffff" aria-hidden="true" className="opacity-80">
                    <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
                  </svg>
                  <svg width="15" height="15" viewBox="0 -960 960 960" fill="#ffffff" aria-hidden="true" className="opacity-80">
                    <path d="M480-200Q339-304 269.5-402T200-594q0-71 25.5-124.5T291-808q40-36 90-54t99-18q49 0 99 18t90 54q40 36 65.5 89.5T760-594q0 94-69.5 192T480-200Zm0-320q33 0 56.5-23.5T560-600q0-33-23.5-56.5T480-680q-33 0-56.5 23.5T400-600q0 33 23.5 56.5T480-520Z" />
                  </svg>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-2.5 px-3.5 py-[13px]">
                  <span className="text-[13px] font-bold tracking-tight">Übersicht der Patientenanfragen</span>

                  <div className="flex flex-col gap-2.5 rounded-md border border-[#e3e6ec] bg-white px-[13px] py-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-bold">Auswertung</span>
                      <span className="flex gap-1">
                        <span className="rounded-full bg-[#d8eefa] px-2 py-0.5 text-[8px] font-semibold text-[#0b6f93]">Gesamt</span>
                        <span className="px-1.5 py-0.5 text-[8px] text-[#7a8292]">2026</span>
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-[22px] gap-y-2.5">
                      <div className="flex-none">
                        <p className="m-0 mb-px text-[9px] text-[#7a8292]">Anfragen gesamt</p>
                        <p className="m-0 text-[21px] font-bold leading-none">12</p>
                      </div>

                      <div className="flex flex-none items-center gap-2.5">
                        <span
                          className="relative block h-[68px] w-[68px] flex-none rounded-full"
                          style={{ background: "conic-gradient(#0099ce 0deg 90deg, #c2700a 90deg 210deg, #1b7f4b 210deg 330deg, #8a94a6 330deg 360deg)" }}
                        >
                          <span className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white">
                            <span className="text-[9px] font-semibold text-[#41495a]">Status</span>
                          </span>
                        </span>
                        <span className="flex flex-col gap-0.5 text-[9px] text-[#41495a]">
                          <span>
                            <strong className="font-bold">3</strong> Neu
                          </span>
                          <span>
                            <strong className="font-bold">4</strong> In Bearbeitung
                          </span>
                          <span>
                            <strong className="font-bold">4</strong> Eingezogen
                          </span>
                          <span>
                            <strong className="font-bold">1</strong> Abgesagt
                          </span>
                        </span>
                      </div>

                      <div className="flex flex-none items-center gap-2.5">
                        <span
                          className="relative block h-[68px] w-[68px] flex-none rounded-full"
                          style={{ background: "conic-gradient(#4aa5f5 0deg 240deg, #6660a6 240deg 360deg)" }}
                        >
                          <span className="absolute left-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white">
                            <span className="text-[9px] font-semibold tracking-tight text-[#41495a]">Standorte</span>
                          </span>
                        </span>
                        <span className="flex flex-col gap-0.5 text-[9px] text-[#41495a]">
                          <span>
                            <strong className="font-bold">8</strong> Seniorenzentrum Neuer Hof
                          </span>
                          <span>
                            <strong className="font-bold">4</strong> Haus Lindenpark
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-md border border-[#e3e6ec] bg-white">
                    <div className="flex items-center gap-[7px] border-b border-[#e3e6ec] px-[11px] py-[7px]">
                      <span className="min-w-0 flex-1 rounded border border-[#e3e6ec] px-[7px] py-[3px] text-[9px] text-[#9aa2b1]">Nach Name suchen</span>
                      <span className="flex-none rounded border border-[#e3e6ec] px-[7px] py-[3px] text-[9px] text-[#596274]">Alle Standorte</span>
                      <span className="flex-none rounded bg-[#0099ce] px-2 py-[3px] text-[9px] font-semibold text-white">Formular einbinden</span>
                    </div>

                    <div className="flex gap-[9px] border-b border-[#e3e6ec] bg-[#f4f6fa] px-[11px] py-1.5">
                      <span className="flex-[1.3] text-[8px] font-semibold uppercase tracking-wide text-[#7a8292]">Name</span>
                      <span className="flex-[1.5] text-[8px] font-semibold uppercase tracking-wide text-[#7a8292]">Standort</span>
                      <span className="flex-1 text-[8px] font-semibold uppercase tracking-wide text-[#7a8292]">Eingegangen</span>
                      <span className="flex-[0.9] text-[8px] font-semibold uppercase tracking-wide text-[#7a8292]">Status</span>
                    </div>

                    <Zeile name="Andrea Hoffmann" standort="Seniorenzentrum Neuer Hof" eingang="heute, 09:12" status="Neu" farbe="#0b6f93" rahmen="#9ad4ea" hervorgehoben />
                    <Zeile name="Jürgen Kowalski" standort="Haus Lindenpark" eingang="gestern, 16:40" status="In Bearbeitung" farbe="#8a5200" rahmen="#e8c48a" />
                    <Zeile name="Marion Terbeck" standort="Seniorenzentrum Neuer Hof" eingang="12.10., 11:05" status="Eingezogen" farbe="#15663c" rahmen="#9ad3b4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="m-0 text-[13px] leading-relaxed text-[#596274]">
        Patientenanfragen sind ein eigener Bereich neben Ihren Bewerbungen. Beides läuft in derselben Oberfläche, mit denselben
        Standorten und denselben Zugriffsrechten.
      </p>
    </div>
  </div>
);

export default PatientRequestsTeaser;
